import React, { useState } from "react";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { SYSTEM_PAGES, useOps } from "@/lib/opsCards";
import { inputCls } from "./FormRenderer";
import FormBuilder from "./FormBuilder";
import DocLibraryConfig from "./DocLibraryConfig";

const TABS = ["link", "forms", "display"];
const lbl = "block font-mono text-[0.6rem] tracking-[0.16em] uppercase text-slate-400 mb-1";

function LinkTab({ cfg, set, cap }) {
  const known = SYSTEM_PAGES.some(([p]) => p === cfg.link_href);
  const [custom, setCustom] = useState(!!cfg.link_href && !known);
  return (
    <div className="space-y-3">
      <label className="block"><span className={lbl}>“Full” opens</span>
        <select value={custom ? "__custom__" : cfg.link_href} data-testid="card-config-link-select" className={inputCls}
          onChange={(e) => { const v = e.target.value; if (v === "__custom__") { setCustom(true); set({ link_href: "" }); } else { setCustom(false); set({ link_href: v }); } }}>
          <option value="">Default{cap.href ? ` (${cap.href})` : " — none"}</option>
          {SYSTEM_PAGES.map(([p, l]) => <option key={p} value={p}>{l} · {p}</option>)}
          <option value="__custom__">Custom URL…</option>
        </select>
      </label>
      {custom && <input value={cfg.link_href} onChange={(e) => set({ link_href: e.target.value })} placeholder="https://… or /admin/…" data-testid="card-config-link-custom" className={inputCls} />}
      <div className="flex gap-4 text-sm text-slate-300">
        {[["new", "New tab"], ["same", "Same tab"]].map(([v, l]) => (
          <label key={v} className="inline-flex items-center gap-1.5"><input type="radio" checked={cfg.link_target === v} onChange={() => set({ link_target: v })} data-testid={`card-config-target-${v}`} /> {l}</label>
        ))}
      </div>
    </div>
  );
}

function FormsTab({ cfg, set }) {
  const { forms } = useOps();
  const [building, setBuilding] = useState(false);
  const attached = cfg.forms.map((id) => forms.find((f) => f.id === id)).filter(Boolean);
  const available = forms.filter((f) => !cfg.forms.includes(f.id));
  const move = (i, d) => { const c = [...cfg.forms]; [c[i], c[i + d]] = [c[i + d], c[i]]; set({ forms: c }); };
  if (building) return <FormBuilder onCancel={() => setBuilding(false)} onSaved={(f) => { set({ forms: [...cfg.forms, f.id] }); setBuilding(false); }} />;
  return (
    <div className="space-y-3">
      <ul className="space-y-1.5" data-testid="card-config-attached">
        {attached.map((f, i) => (
          <li key={f.id} data-testid={`card-config-attached-${f.id.replace(":", "-")}`} className="flex items-center gap-2 rounded-md border border-slate-800 bg-navy-950/60 px-3 py-2 text-sm text-slate-200">
            <span className="flex-1 truncate">{f.name} <span className="text-slate-500 text-xs">· {f.kind === "builtin" ? "built-in" : "custom"}</span></span>
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 text-slate-400 disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === attached.length - 1} aria-label="Move down" className="p-1 text-slate-400 disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
            <button type="button" onClick={() => set({ forms: cfg.forms.filter((x) => x !== f.id) })} aria-label="Detach" data-testid={`card-config-detach-${f.id.replace(":", "-")}`} className="p-1 text-slate-500 hover:text-rose-300"><X className="w-3 h-3" /></button>
          </li>
        ))}
        {!attached.length && <li className="text-sm text-slate-500">No forms attached — “Pop” keeps its default action.</li>}
      </ul>
      {cfg.forms.includes("builtin:documents") && <DocLibraryConfig docs={cfg.docs} onChange={(docs) => set({ docs })} />}
      <div className="flex gap-2">
        <select value="" onChange={(e) => e.target.value && set({ forms: [...cfg.forms, e.target.value] })} data-testid="card-config-attach-select" className={`${inputCls} flex-1`}>
          <option value="">+ Attach a form…</option>
          {available.map((f) => <option key={f.id} value={f.id}>{f.name}{f.kind === "builtin" ? " (built-in)" : ""}</option>)}
        </select>
        <button type="button" onClick={() => setBuilding(true)} data-testid="card-config-new-form"
          className="inline-flex items-center gap-1 px-3 rounded-md border border-dashed border-brand-cyan/50 text-brand-cyan font-mono text-[0.6rem] tracking-[0.14em] uppercase hover:bg-brand-cyan/10"><Plus className="w-3 h-3" /> New</button>
      </div>
    </div>
  );
}

function DisplayTab({ cfg, set, cap }) {
  const { forms } = useOps();
  const customs = forms.filter((f) => f.kind === "custom");
  return (
    <div className="space-y-3">
      <label className="block"><span className={lbl}>Title</span><input value={cfg.label ?? ""} placeholder={cap.label} onChange={(e) => set({ label: e.target.value })} data-testid="card-config-label" className={inputCls} /></label>
      <label className="block"><span className={lbl}>Description</span><textarea rows={2} value={cfg.description ?? ""} placeholder={cap.description} onChange={(e) => set({ description: e.target.value })} data-testid="card-config-description" className={inputCls} /></label>
      <label className="block"><span className={lbl}>Status pill</span><input value={cfg.status ?? ""} placeholder={cap.status} onChange={(e) => set({ status: e.target.value })} data-testid="card-config-status" className={inputCls} /></label>
      <label className="block"><span className={lbl}>Live counter</span>
        <select value={cfg.counter} onChange={(e) => set({ counter: e.target.value })} data-testid="card-config-counter" className={inputCls}>
          <option value="none">None</option><option value="credit_balance">Credit balance</option>
          <option value="entries_week" disabled={!customs.length}>Entries this week (custom form)</option>
        </select>
      </label>
      {cfg.counter === "entries_week" && (
        <select value={cfg.counter_form || ""} onChange={(e) => set({ counter_form: e.target.value })} data-testid="card-config-counter-form" className={inputCls}>
          <option value="">Choose form…</option>
          {customs.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      )}
    </div>
  );
}

// Owner-only “Configure Card” window: Link · Quick Forms · Display.
export default function CardConfigContent({ win }) {
  const { cards, reload } = useOps();
  const cap = win.payload.cap;
  const saved = cards[cap.id] || {};
  const [cfg, setCfg] = useState({ link_href: "", link_target: "new", forms: [], label: "", description: "", status: "", counter: "none", counter_form: null, ...saved });
  const [tab, setTab] = useState("link");
  const [busy, setBusy] = useState(false);
  const set = (p) => setCfg((c) => ({ ...c, ...p }));
  const save = async () => {
    setBusy(true);
    try {
      const { cap_id, updated_at, updated_by, ...body } = cfg; // eslint-disable-line no-unused-vars
      await apiClient.put(`/admin/ops/cards/${cap.id}`, { ...body, label: body.label || null, description: body.description || null, status: body.status || null });
      toast.success("Card saved"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="card-config">
      <div className="flex gap-1 mb-4">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} data-testid={`card-config-tab-${t}`}
            className={`px-3 py-1.5 rounded-md font-mono text-[0.6rem] tracking-[0.14em] uppercase border transition-colors ${tab === t ? "border-brand-cyan/60 bg-brand-cyan/10 text-brand-cyan" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
            {t === "forms" ? "Quick forms" : t}
          </button>
        ))}
      </div>
      {tab === "link" && <LinkTab cfg={cfg} set={set} cap={cap} />}
      {tab === "forms" && <FormsTab cfg={cfg} set={set} />}
      {tab === "display" && <DisplayTab cfg={cfg} set={set} cap={cap} />}
      <button type="button" onClick={save} disabled={busy} data-testid="card-config-save"
        className="mt-5 w-full px-4 py-2.5 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.65rem] font-bold tracking-[0.16em] uppercase hover:brightness-110 disabled:opacity-60">
        {busy ? "Saving…" : "Save card"}
      </button>
    </div>
  );
}
